import { useEffect, useMemo, useState } from "react";
import { useLang } from "../lib/useLang";
import { useCart } from "../state/CartContext";
import { useSheet } from "../state/SheetContext";
import { formatKWD } from "../lib/format";
import { optionsForCategory, unitPriceWith } from "../data/modifiers";
import type { ModifierGroup, SelectedModifier } from "../lib/types";
import { Money } from "./Money";
import { QtyStepper } from "./QtyStepper";
import { SmartImage } from "./SmartImage";

type Selection = Record<string, string[]>; // groupId -> chosen option ids

/** Default selection: single-select groups start on their first option. */
function defaultSelection(groups: ModifierGroup[]): Selection {
  const sel: Selection = {};
  for (const g of groups) {
    sel[g.id] = g.type === "single" && g.options[0] ? [g.options[0].id] : [];
  }
  return sel;
}

function buildModifiers(groups: ModifierGroup[], sel: Selection): SelectedModifier[] {
  const out: SelectedModifier[] = [];
  for (const g of groups) {
    for (const optId of sel[g.id] ?? []) {
      const opt = g.options.find((o) => o.id === optId);
      if (!opt) continue;
      out.push({
        groupId: g.id,
        groupName: g.name,
        groupNameAr: g.nameAr,
        optionId: opt.id,
        optionName: opt.name,
        optionNameAr: opt.nameAr,
        priceDelta: opt.priceDelta,
      });
    }
  }
  return out;
}

/** Tap-to-open item detail: big photo, options, quantity, notes, add. */
export function ItemSheet() {
  const { item, close } = useSheet();
  const { t, lang, pick } = useLang();
  const { add } = useCart();
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const [sel, setSel] = useState<Selection>({});

  // Standing items get category-based option groups; daily items don't.
  const groups = useMemo<ModifierGroup[]>(
    () =>
      item && item.type === "standing" && item.category
        ? (optionsForCategory(item.category) ?? [])
        : [],
    [item],
  );

  useEffect(() => {
    setQty(1);
    setNotes("");
    setSel(defaultSelection(groups));
    // Re-init whenever a different item opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.itemId]);

  // Lock body scroll + close on Escape while open.
  useEffect(() => {
    if (!item) return;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [item, close]);

  const mods = useMemo(() => buildModifiers(groups, sel), [groups, sel]);
  const unitPrice = item ? unitPriceWith(item.price, mods) : 0;
  const valid = groups.every((g) => !g.required || (sel[g.id]?.length ?? 0) > 0);

  if (!item) return null;
  const soldOut = item.soldOut;

  function toggle(group: ModifierGroup, optionId: string) {
    setSel((prev) => {
      const current = prev[group.id] ?? [];
      if (group.type === "single") {
        return { ...prev, [group.id]: [optionId] };
      }
      // multi: toggle, honouring an optional max
      if (current.includes(optionId)) {
        return { ...prev, [group.id]: current.filter((id) => id !== optionId) };
      }
      if (group.max && current.length >= group.max) return prev;
      return { ...prev, [group.id]: [...current, optionId] };
    });
  }

  function addToCart() {
    if (!item || soldOut || !valid) return;
    add(
      {
        itemId: item.itemId,
        type: item.type,
        name: item.name,
        nameAr: item.nameAr,
        price: unitPrice,
        basePrice: item.price,
        imageURL: item.imageURL,
        session: item.session,
        fastDelivery: item.fastDelivery,
        notes: notes.trim() || undefined,
        modifiers: mods.length ? mods : undefined,
      },
      qty,
    );
    close();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4"
      onClick={close}
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="fade-up relative max-h-[92vh] w-full max-w-md overflow-y-auto overflow-x-hidden rounded-t-3xl bg-white shadow-raised sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`img-skeleton relative h-48 ${soldOut ? "grayscale" : ""}`}>
          <SmartImage
            src={item.imageURL}
            alt={pick(item.name, item.nameAr)}
            eager
            className="h-full w-full object-cover"
            fallback={
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand/15 to-gold/15 text-6xl">
                🍽️
              </div>
            }
          />
          <button
            onClick={close}
            aria-label="close"
            className="absolute end-3 top-3 grid size-9 place-items-center rounded-full bg-white/90 text-lg shadow-soft"
          >
            ✕
          </button>
          <div className="absolute inset-x-3 bottom-3 flex gap-1.5">
            {item.fastDelivery && (
              <span className="chip bg-white/90 text-brand shadow-sm">⚡ {t("common.fast")}</span>
            )}
            {item.popular && (
              <span className="chip bg-white/90 text-brand shadow-sm">🔥 {t("common.popular")}</span>
            )}
          </div>
        </div>

        <div className="p-5">
          <h3 className="display text-xl text-ink">{pick(item.name, item.nameAr)}</h3>
          <div className="text-sm text-black/45" dir="auto">
            {pick(item.nameAr, item.name)}
          </div>
          <Money value={item.price} className="price mt-1 block text-lg" />

          {/* Option groups */}
          {groups.map((g) => (
            <div key={g.id} className="mt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-ink">{pick(g.name, g.nameAr)}</span>
                <span className="text-xs font-semibold text-black/40">
                  {g.required
                    ? t("sheet.required")
                    : g.max
                      ? t("sheet.upTo", { n: g.max })
                      : t("sheet.optional")}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {g.options.map((o) => {
                  const on = (sel[g.id] ?? []).includes(o.id);
                  return (
                    <button
                      key={o.id}
                      onClick={() => toggle(g, o.id)}
                      className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                        on
                          ? "border-brand bg-brand text-white"
                          : "border-black/10 bg-white text-gray-700 hover:border-brand/40"
                      }`}
                    >
                      {pick(o.name, o.nameAr)}
                      {o.priceDelta > 0 && (
                        <span className={on ? "text-white/80" : "text-black/45"}>
                          {" "}
                          +{formatKWD(o.priceDelta, lang)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Notes only for standing (made-to-order) items. Daily bulk items
              are pre-cooked and must stay one line per id so limited stock
              can't be oversold via note-variant cart lines. */}
          {item.type === "standing" && (
            <>
              <label className="mt-4 block text-xs font-semibold text-black/55">
                {t("sheet.notes")}
              </label>
              <textarea
                className="input mt-1"
                rows={2}
                placeholder={t("sheet.notesPh")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </>
          )}

          <div className="mt-4 flex items-center gap-3">
            <QtyStepper qty={qty} max={item.maxQty} onChange={(q) => setQty(Math.max(1, q))} />
            <button
              className="btn-primary flex-1"
              disabled={soldOut || !valid}
              onClick={addToCart}
            >
              {soldOut
                ? t("common.soldOut")
                : `${t("sheet.add")} · ${formatKWD(unitPrice * qty, lang)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
